import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

import type { Period } from "@/components/PeriodTabs";
import type { DateRange } from '@/lib/dateRange';

import { apiGet } from "./client";

/**
 * GET /api/v2/ozet?days= — sozlesme §4 (backend/app/core/api/ozet.py +
 * app/core/analytics/gidisat.py `veri_paketi`). Semalar KASITLI GEVSEK:
 * her bolum `status` disinda "error" durumunda cogu alani hic gondermez
 * (bkz. gidisat.py `pazarlama_derle`/`trafik_derle`/vb.) — bu yuzden her
 * metrik alani `.optional()`, her bolum `.passthrough()`.
 */

export const kSchema = z.object({
  cur: z.number().nullable(),
  prev: z.number().nullable(),
  delta_pct: z.number().nullable(),
});
export type K = z.infer<typeof kSchema>;

export const hedefAlanSchema = z.object({
  hedef_aylik: z.number(),
  hedef_donem: z.number(),
  gerceklesen: z.number(),
  oran_pct: z.number().nullable(),
});
export type HedefAlan = z.infer<typeof hedefAlanSchema>;

export const gunlukSatisSchema = z.object({ gun: z.string(), ciro: z.number(), siparis: z.number() });
export const haftalikSatisSchema = z
  .object({
    hafta: z.string(),
    ciro: z.number(),
    siparis: z.number(),
    aov: z.number().nullable(),
    wow_pct: z.number().nullable(),
    tam_hafta: z.boolean(),
  })
  .passthrough();
export const aylikSatisSchema = z
  .object({
    ay: z.string(),
    ciro: z.number(),
    siparis: z.number(),
    aov: z.number().nullable(),
    mom_pct: z.number().nullable(),
    tam_ay: z.boolean(),
  })
  .passthrough();

export const satisSchema = z
  .object({
    status: z.string(),
    definition: z.string().optional(),
    hata: z.string().optional(),
    ciro: kSchema.optional(),
    siparis: kSchema.optional(),
    aov: kSchema.optional(),
    iptal_orani_pct: kSchema.optional(),
    yeni_musteri: kSchema.optional(),
    tekrar_orani_pct: kSchema.optional(),
    iade_tutar: kSchema.optional(),
    gunluk: z.array(gunlukSatisSchema).optional(),
    onceki_gunluk: z.array(gunlukSatisSchema).optional(),
    bugun: z
      .object({ ciro: z.number(), siparis: z.number(), dun_ciro: z.number(), pct: z.number().nullable() })
      .optional(),
    haftalik: z.array(haftalikSatisSchema).optional(),
    aylik: z.array(aylikSatisSchema).optional(),
    hedef: z.object({ ciro: hedefAlanSchema.nullable(), siparis: hedefAlanSchema.nullable() }).nullable().optional(),
  })
  .passthrough();
export type Satis = z.infer<typeof satisSchema>;

export const metaOzetSchema = z
  .object({
    status: z.string(),
    harcama: kSchema.optional(),
    satin_alma: kSchema.optional(),
    roas: kSchema.optional(),
    veri_gunu: z.object({ cur: z.number(), prev: z.number() }).optional(),
  })
  .passthrough();

export const pazarlamaSchema = z
  .object({
    status: z.string(),
    hata: z.string().optional(),
    prev_kapsam_yetersiz: z.boolean().optional(),
    scope_note: z.string().optional(),
    harcama: kSchema.optional(),
    reklam_satis_degeri: kSchema.optional(),
    roas: kSchema.optional(),
    mer: kSchema.optional(),
    reklam_maliyet_orani_pct: kSchema.optional(),
    tiklama: kSchema.optional(),
    meta: metaOzetSchema.optional(),
    google: z.object({ status: z.string() }).passthrough().optional(),
    gunluk_harcama: z.array(z.object({ gun: z.string(), meta: z.number(), google: z.number().nullable() })).optional(),
  })
  .passthrough();
export type Pazarlama = z.infer<typeof pazarlamaSchema>;

export const trafikSchema = z
  .object({
    status: z.string(),
    hata: z.string().optional(),
    oturum: kSchema.optional(),
    kullanici: kSchema.optional(),
    donusum_orani_pct: kSchema.optional(),
    huni: z.array(z.object({ label: z.string(), count: z.number() }).passthrough()).optional(),
  })
  .passthrough();
export type Trafik = z.infer<typeof trafikSchema>;

export const musteriSchema = z
  .object({
    status: z.string(),
    hata: z.string().optional(),
    yorum_adet: kSchema.optional(),
    ort_puan: kSchema.optional(),
    dusuk_puan_adet: kSchema.optional(),
    destek_acilan: kSchema.optional(),
    destek_bekleyen: z.number().nullable().optional(),
    destek_bekleyen_pg: z.number().nullable().optional(),
    iade_talep: kSchema.optional(),
  })
  .passthrough();
export type Musteri = z.infer<typeof musteriSchema>;

export const operasyonSchema = z
  .object({
    status: z.string(),
    hata: z.string().optional(),
    teslim_gun: kSchema.optional(),
    teslim_medyan_gun: kSchema.optional(),
    yolda_14g_ustu: z.number().nullable().optional(),
    kargo_tarihi_bilinmeyen: z.number().nullable().optional(),
    teslim_olculen: z.number().optional(),
    kargolanmamis: z.number().nullable().optional(),
    kargo_yolda: z.number().nullable().optional(),
    kritik_stok: z.number().nullable().optional(),
    tukenen: z.number().nullable().optional(),
    kritik_stok_alarm: z.string().nullable().optional(),
    saglik: z.object({ cur: z.number().nullable(), prev: z.number().nullable() }).optional(),
  })
  .passthrough();
export type Operasyon = z.infer<typeof operasyonSchema>;

export const hukumSchema = z.object({ cls: z.string(), ikon: z.string(), metin: z.string() }).passthrough();
export type Hukum = z.infer<typeof hukumSchema>;

export const hukumlerSchema = z
  .object({
    satis: hukumSchema,
    pazarlama: hukumSchema,
    musteri: hukumSchema,
    operasyon: hukumSchema,
    genel: hukumSchema,
  })
  .partial()
  .passthrough();
export type Hukumler = z.infer<typeof hukumlerSchema>;

export const tazelikGirdiSchema = z.object({
  kaynak: z.string(),
  last_sync: z.string().nullable(),
  bayat: z.boolean(),
});

export const veriKalitesiSchema = z
  .object({
    uyarilar: z.array(z.string()),
    tazelik: z.array(tazelikGirdiSchema),
  })
  .passthrough();

export const donemSchema = z.object({
  gun: z.number(),
  bas: z.string(),
  bit: z.string(),
  onceki_bas: z.string(),
  onceki_bit: z.string(),
  tz: z.string(),
});
export type Donem = z.infer<typeof donemSchema>;

export const ozetResponseSchema = z
  .object({
    uretim: z.string(),
    donem: donemSchema,
    satis: satisSchema,
    pazarlama: pazarlamaSchema,
    trafik: trafikSchema,
    musteri: musteriSchema,
    operasyon: operasyonSchema,
    hukum: hukumlerSchema,
    veri_kalitesi: veriKalitesiSchema,
  })
  .passthrough();
export type OzetResponse = z.infer<typeof ozetResponseSchema>;

export function fetchOzet(days: Period, range?: DateRange): Promise<OzetResponse> {
  const query = range ? `start=${range.start}&end=${range.end}` : `days=${days}`;
  return apiGet(`/api/v2/ozet?${query}`, ozetResponseSchema);
}

export function ozetQueryKey(days: Period) {
  return ["ozet", days] as const;
}

export function useOzet(days: Period, range?: DateRange) {
  return useQuery({
    queryKey: range ? ['ozet', range.start, range.end] : ozetQueryKey(days),
    queryFn: () => fetchOzet(days, range),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    // Eski refresh.js: sekme acikken 15 dk'da bir arka plan yenileme (duvar ekrani).
    refetchInterval: 15 * 60_000,
  });
}
